// Translates between the opaque MUI DataGrid state the asset-view/factors slices
// persist (`datagridState`, a `GridInitialState`) and the portable subset the
// layout contract carries (`orderedFields` / `pinnedColumns` / `sortModel`).
//
// Only column order, pinning, and sort travel in a layout — they are
// user-meaningful arrangement. Column widths/dimensions stay instance-only
// (per-screen ergonomics) and are never read here. See docs/designs/layout-config-portability.md.
import type { AssetViewConfig } from "../contracts/layouts"

// The contract fields shared by asset-view (all three) and factors (order + pin).
export type PortableGrid = Pick<AssetViewConfig, "orderedFields" | "pinnedColumns" | "sortModel">

type SortItem = NonNullable<PortableGrid["sortModel"]>[number]

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string")
}

// datagridState is typed `any` in the slices (opaque MUI shape), and on import it
// crosses the persisted-JSON boundary — so narrow every field with typeof rather
// than trusting the shape.
function readSortModel(value: unknown): PortableGrid["sortModel"] {
  if (!Array.isArray(value)) return undefined
  const items: SortItem[] = []
  for (const item of value) {
    if (typeof item !== "object" || item === null) continue
    const { field, sort } = item as { field?: unknown, sort?: unknown }
    if (typeof field !== "string") continue
    items.push(sort === "asc" || sort === "desc" || sort === null ? { field, sort } : { field })
  }
  return items.length > 0 ? items : undefined
}

function readPinnedColumns(value: unknown): PortableGrid["pinnedColumns"] {
  if (typeof value !== "object" || value === null) return undefined
  const { left, right } = value as { left?: unknown, right?: unknown }
  const pinned: NonNullable<PortableGrid["pinnedColumns"]> = {}
  if (isStringArray(left) && left.length > 0) pinned.left = [...left]
  if (isStringArray(right) && right.length > 0) pinned.right = [...right]
  return pinned.left || pinned.right ? pinned : undefined
}

/** Extract the portable arrangement subset from a slice's opaque `datagridState`. */
export function extractPortableGrid(datagridState: unknown): PortableGrid {
  if (typeof datagridState !== "object" || datagridState === null) return {}
  const state = datagridState as { columns?: unknown, pinnedColumns?: unknown, sorting?: unknown }

  const grid: PortableGrid = {}

  const columns = state.columns
  const orderedFields = typeof columns === "object" && columns !== null
    ? (columns as { orderedFields?: unknown }).orderedFields
    : undefined
  if (isStringArray(orderedFields) && orderedFields.length > 0) {
    grid.orderedFields = [...orderedFields]
  }

  const pinnedColumns = readPinnedColumns(state.pinnedColumns)
  if (pinnedColumns) grid.pinnedColumns = pinnedColumns

  const sortModel = typeof state.sorting === "object" && state.sorting !== null
    ? readSortModel((state.sorting as { sortModel?: unknown }).sortModel)
    : undefined
  if (sortModel) grid.sortModel = sortModel

  return grid
}

/**
 * Build the `datagridState` to seed a panel slice from a layout's portable grid
 * fields. Produces only the order/pin/sort slices of a MUI `GridInitialState`
 * (typed as the slices' opaque storage shape); widths are left to the viewer's
 * own instance state. The grid drops references to columns it does not have on
 * apply (the dangling-reference rule), so unknown field ids here are harmless.
 */
export function portableGridToDatagridState(grid: PortableGrid): Record<string, unknown> {
  const state: Record<string, unknown> = {}
  if (grid.orderedFields) state.columns = { orderedFields: [...grid.orderedFields] }
  if (grid.pinnedColumns) state.pinnedColumns = { ...grid.pinnedColumns }
  if (grid.sortModel) state.sorting = { sortModel: grid.sortModel.map((item) => ({ ...item })) }
  return state
}
